import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import ChipArrayField from '../forms/ChipArrayField';

type Props = {
  disabled: boolean;
  rules?: Parameters<typeof Controller>[0]['rules'];
};

type LciCampaignRow = {
  id?: number;
  lci_campaign_id: number | null | undefined;
  campaign_type?: 'internal' | 'external';
  active?: boolean;
  '_destroy'?: '1';
};

const adapt = {
  toStrings: (rows: LciCampaignRow[]) =>
    (rows ?? [])
      // eslint-disable-next-line no-underscore-dangle
      .filter((r) => r._destroy !== '1' && r.active !== false)
      .map((r) => (r.lci_campaign_id != null ? String(r.lci_campaign_id) : ''))
      .filter(Boolean),
  toRows: (ids: string[], original: LciCampaignRow[]) => {
    const nextIds = Array.from(
      new Set(
        ids
          .flatMap((txt) => txt.split(/[\s,]+/))
          .filter((t) => /^\d+$/.test(t))
          .map(Number)
      )
    );

    const persisted = original ?? [];

    const reconciledPersisted = (persisted.map((row) => {
      if (row.id) {
        const keep = nextIds.includes(row.lci_campaign_id as number);
        return keep
          ? {
              ...row,
              active: true,
              // eslint-disable-next-line no-underscore-dangle
              _destroy: undefined,
            }
          : {
              ...row,
              // eslint-disable-next-line no-underscore-dangle
              _destroy: '1',
            };
      }
      const keep = nextIds.includes(row.lci_campaign_id as number);
      return keep ? { ...row, active: true } : null;
    }).filter(Boolean) as LciCampaignRow[]);

    const existingIds = new Set(
      reconciledPersisted
        // eslint-disable-next-line no-underscore-dangle
        .filter((r) => r._destroy !== '1')
        .map((r) => r.lci_campaign_id)
    );
    const newRows = nextIds
      .filter((id) => !existingIds.has(id))
      .map((id) => ({
        lci_campaign_id: id,
        campaign_type: 'internal',
        active: true,
      } as LciCampaignRow));

    return [...reconciledPersisted, ...newRows];
  },
};

function LciCampaignChipField({ disabled, rules }: Props) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name="contractLciCampaigns"
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <ChipArrayField
          label="LCI Campaign Id"
          placeholder="Add LCI Campaign Id"
          disabled={disabled}
          validateInput={(txt) => /^\d+$/.test(txt)}
          value={adapt.toStrings(field.value ?? [])}
          onChange={(ids) => {
            const next = adapt.toRows(ids, field.value ?? []);
            setValue('contractLciCampaigns', next, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message as string}
        />
      )}
    />
  );
}

export default LciCampaignChipField;
